import { IWixAPI } from '@wix/yoshi-flow-editor';
import {
  getAppConfig,
  getCategories,
  getCategoryBySlug,
  getCategoryByTranslationSlug,
  NormalizedCategory,
  SECTION_CATEGORY,
} from '@wix/communities-blog-client-common';
import { navigateWithinBlog } from '../../../common/actions/navigate-within-blog';
import { pageOpened } from '../../../common/actions/page-opened';
import { isPostContentRequired } from '../../../common/selectors/layout-selectors';
import { getQueryLocale } from '../../../common/selectors/locale-selectors';
import {
  getIsValidPage,
  getLazyPaginationParams,
  getShowPagination,
} from '../../../common/selectors/pagination-selectors';
import { translate } from '../../../common/services/controller-translate';
import { CATEGORY_PAGE } from '../../../common/services/detect-route';
import { generateCategorySEOTags } from '../../../common/services/generate-seo-tags/generate-category-seo-tags';
import { resolveCategorySlug } from '../../../common/services/slug';
import {
  getBasicParams,
  isSite,
  isSSR,
} from '../../../common/store/basic-params/basic-params-selectors';
import { fetchProfileUrls } from '../../../common/store/profile-urls/profile-urls-actions';
import { getSectionUrl } from '../../../common/store/topology/topology-selectors';
import { AppStore, RouteResolverFn } from '../../../common/types';
import { fetchCategoryFeedRenderModel } from '../../actions/fetch-feed-render-model';
import { ROUTE_404, RouteCategoryParams } from '../../constants/routes';

export const createCategoryPageRouterWithAdapter = (
  store: AppStore,
  wixCodeApi: IWixAPI,
): RouteResolverFn<RouteCategoryParams> => {
  return async ({ params }, redirect, { preFetch, preFetchResult }) => {
    const page = parseInt(params.page || '1', 10);
    const slug = resolveCategorySlug(params);
    const basicParams = getBasicParams(store.getState());
    const categories = getCategories(store.getState());

    const actions = preFetchResult || [
      store.dispatch(
        fetchCategoryFeedRenderModel({
          page,
          categoryOptions: { id: slug },
          pageSize: getLazyPaginationParams({
            state: store.getState(),
            section: SECTION_CATEGORY,
            page,
          })?.size,
          includeContent: isPostContentRequired(
            store.getState(),
            SECTION_CATEGORY,
          ),
          includeInitialPageData: categories.length === 0,
          language: basicParams.language,
        }),
      ),
    ];

    if (preFetch) {
      return Promise.all(actions);
    }

    if (preFetchResult) {
      await preFetchResult;
      store.dispatch(fetchProfileUrls());
    } else {
      await Promise.all(actions);
    }

    const i18nCategorySlug = getI18nCategorySlug(slug);

    if (i18nCategorySlug) {
      return store.dispatch(
        navigateWithinBlog(`/categories/${i18nCategorySlug}`),
      );
    }

    const category = getCategoryBySlug(store.getState(), slug);

    if (isInvalidPage() || !category) {
      return redirect(ROUTE_404);
    }

    await loadFeedSEOTagsIfMissing(page, category);

    trackPageOpen(category);
  };

  function getI18nCategorySlug(slug: string) {
    const category = getCategoryBySlug(store.getState(), slug);
    const lang = getQueryLocale(store.getState());

    if (lang && !category) {
      const i18nCategory = getCategoryByTranslationSlug(
        store.getState(),
        slug,
        lang,
      );

      return i18nCategory?.slug;
    }
  }

  function isInvalidPage() {
    const state = store.getState();
    const showPagination = getShowPagination(state, SECTION_CATEGORY);
    const isValidPage = getIsValidPage(state, SECTION_CATEGORY);
    return showPagination && !isValidPage;
  }

  async function loadFeedSEOTagsIfMissing(
    page: number,
    category: NormalizedCategory,
  ) {
    const state = store.getState();

    if (!isSite(state)) {
      return;
    }

    const categorySEOTags = await generateCategorySEOTags({
      appConfig: getAppConfig(state),
      sectionUrl: getSectionUrl(state),
      showPagination: getShowPagination(state, SECTION_CATEGORY),
      category,
      store,
      page,
      t: translate,
      multilingual: wixCodeApi.window.multilingual,
    });
    wixCodeApi.seo.resetSEOTags();
    wixCodeApi.seo.renderSEOTags(categorySEOTags);
  }

  function trackPageOpen(category: NormalizedCategory) {
    const state = store.getState();

    if (isSSR(state)) {
      return;
    }

    store.dispatch(
      pageOpened({
        page: CATEGORY_PAGE,
        category,
      }),
    );
  }
};
